import { HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'redux/store';

interface SyncProgress {
  processed: number;
  total: number;
}

type SyncStatus = 'idle' | 'syncing' | 'complete';

const baseUrl = process.env.REACT_APP_JULIA_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_JULIA || '';

export const useCatalogSync = (divisionId: string | undefined, onSyncComplete: () => void): { syncStatus: SyncStatus; syncProgress: SyncProgress } => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({ processed: 0, total: 0 });
  const accessToken = useAppSelector((state) => state.app.accessToken);

  useEffect(() => {
    if (!divisionId || !accessToken) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/signalr?divisionId=${divisionId}`, {
        accessTokenFactory: () => accessToken,
        headers: { 'x-functions-key': functionsKey },
        withCredentials: false,
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
      })
      .withAutomaticReconnect()
      .build();

    connection.on('catalogSyncStarted', (data: { totalItems: number }) => {
      setSyncStatus('syncing');
      setSyncProgress({ processed: 0, total: data.totalItems });
    });

    connection.on('catalogSyncProgress', (data: { processed: number; total: number }) => {
      setSyncProgress({ processed: data.processed, total: data.total });
    });

    connection.on('catalogSyncComplete', () => {
      setSyncStatus('complete');
      onSyncComplete();
      setTimeout(() => setSyncStatus('idle'), 2000);
    });

    connection.on('catalogItemUpdated', () => {
      onSyncComplete();
    });

    connection
      .start()
      .then(async () => {
        const connectionId = connection.connectionId;

        if (connectionId) {
          await fetch(`${baseUrl}/signalr/join-group?connectionId=${encodeURIComponent(connectionId)}&divisionId=${encodeURIComponent(divisionId)}`, {
            method: 'POST',
            headers: {
              'x-functions-key': functionsKey,
              Authorization: `Bearer ${accessToken}`
            }
          });
        }
      })
      .catch((err: unknown) => console.error('SignalR connection failed:', err));

    return (): void => {
      connection.stop();
    };
  }, [divisionId, accessToken, onSyncComplete]);

  return { syncStatus, syncProgress };
};
